import type { FamilySignals } from '@fraudeai/schema';
import { defineModule } from '../harness.js';
import { attempt } from '../util/safe.js';

/**
 * Признаки семейства движка.
 *
 * Здесь собраны свойства, которые существуют ровно в одном семействе
 * браузеров и отсутствуют во всех прочих. Ценность их в том, что они
 * отвечают на вопрос «какой это движок на самом деле», не спрашивая об этом
 * сам браузер: User-Agent правится строкой, а наличие `ApplePaySession` в
 * сборке на Chromium не появится, сколько его ни переписывай.
 *
 * Именно этого слоя нам не хватало. Антидетект-сборки делают на Chromium и
 * выдают за что угодно; отпечаток TLS у них при этом настоящий, хромовский,
 * и сетевой слой их не ловит. А вот набор служебных свойств выдаёт: браузер,
 * называющий себя Safari, обязан иметь всё то, что есть у WebKit.
 *
 * Значения намеренно сырые. Решение принимает правило на сервере, а не
 * страница: иначе достаточно было бы прислать удобный вывод.
 */

/**
 * Длина исходного текста `eval`.
 *
 * Классический различитель движков: возвращается длина строки, которую
 * движок печатает вместо тела встроенной функции. У V8 это 33 символа, у
 * JavaScriptCore и SpiderMonkey — 37. Число задано способом печати внутри
 * движка и одинаково у всех сборок на нём.
 */
function evalLength(): number | null {
  return attempt(() => {
    // eslint-disable-next-line no-eval
    const source = String(eval);
    return source.length;
  });
}

export const familyModule = defineModule({
  id: 'family',
  phase: 1,
  timeoutMs: 1500,
  run(): FamilySignals {
    const w = window as unknown as Record<string, unknown>;

    return {
      evalLength: evalLength(),

      // Только WebKit. Присутствует в Safari на macOS и iOS и отсутствует в
      // Chromium любой сборки — в том числе в тех, что выдают себя за Safari.
      applePay: attempt(() => typeof w.ApplePaySession === 'function') ?? null,

      // Только Gecko. `toSource` — нестандартный метод, оставшийся от
      // ранних версий SpiderMonkey; ни V8, ни JavaScriptCore его не имеют.
      errorToSource: attempt(() => typeof (Error.prototype as never)['toSource'] === 'function') ?? null,

      // Только Gecko, и то в сборках до 2021 года. Отсутствие ничего не
      // значит, зато наличие однозначно указывает на Firefox.
      installTrigger: attempt(() => 'InstallTrigger' in w) ?? null,

      // Только Chromium. Объект `performance.memory` нестандартный, в WebKit
      // и Gecko его нет. Предел кучи заодно связан с объёмом памяти машины.
      heapSizeLimit: attempt(() => {
        const memory = (performance as unknown as Record<string, unknown>).memory as
          | { jsHeapSizeLimit?: number }
          | undefined;
        return typeof memory?.jsHeapSizeLimit === 'number' ? memory.jsHeapSizeLimit : null;
      }),

      // Наследие Internet Explorer. В современных браузерах отсутствует, и
      // наличие означает либо очень старую сборку, либо грубую подделку.
      cpuClass: attempt(() => {
        const value = (navigator as unknown as Record<string, unknown>).cpuClass;
        return typeof value === 'string' ? value : null;
      }),

      // Разделяемая память доступна только при изоляции источника. Мы её не
      // включаем, поэтому у настоящего браузера ожидается отсутствие; наличие
      // говорит о нестандартных настройках сборки.
      sharedArrayBuffer: attempt(() => typeof w.SharedArrayBuffer === 'function') ?? null,

      // Набор поддерживаемых типов для потокового воспроизведения. Зависит от
      // набора кодеков, вшитых в сборку, и различается между семействами.
      sourceBufferTypes: attempt(() => {
        const source = w.MediaSource as { isTypeSupported?: (t: string) => boolean } | undefined;
        if (typeof source?.isTypeSupported !== 'function') return null;
        const probes = [
          'video/mp4; codecs="avc1.42E01E"',
          'video/webm; codecs="vp9"',
          'audio/mp4; codecs="mp4a.40.2"',
          'video/mp4; codecs="hvc1.1.6.L93.B0"',
        ];
        return probes.filter((type) => source.isTypeSupported!(type));
      }),
    };
  },
});
